'use client';
import { useDebounceFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type DependencyList, Fragment, useEffect, useRef } from 'react';
import { AutoSizer, Grid } from 'react-virtualized';
import { BaseFillCheckPick3 } from '@/app/components/icons/BaseFillCheckPick3';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { MediaPromptPreview } from '@/app/components/shared/MediaPromptPreview';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { SearchEmpty } from '@/app/components/shared/SearchEmpty';
import { TextElementCover } from '@/app/components/shared/TextElementCover';
import { CheckboxFill } from '@/app/components/ui/checkbox-fill';
import { SEARCH_EMPTY_ICON, SITE } from '@/app/constants';
import { useCheckEnableCreateElement, useExpose, useExtraTag, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ElementType, TagType } from '@/app/types';
import { cn, convertComponents2Assets, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';

const GRID_PARAMS = {
  columnCount: 1,
  rowHeight: 72,
};

interface ElementListProps {
  className?: string;
  selectedElements: ElementType[];
  tagSelectors?: string[];
  disabledIds?: string[];
  tags?: TagType[];
  elements: ElementType[];
  isLoading?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  enableCreate?: boolean;
  toggleElementSelection?: (element: ElementType) => void;
  deeps?: DependencyList;
  onExpose?: (idx: number) => void;
  onCreateOpen?: () => void;
}

export const ElementList = ({
  className,
  selectedElements,
  tagSelectors,
  disabledIds,
  tags,
  elements,
  isLoading = false,
  hasNextPage = false,
  enableCreate = true,
  onLoadMore,
  toggleElementSelection,
  deeps,
  onExpose,
  onCreateOpen,
}: ElementListProps) => {
  const { allTag } = useExtraTag();
  const t = useTranslations();
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const pathname = usePathname();
  const { openReferenceModalWithData, userInfo } = useStore((state) => ({
    openReferenceModalWithData: state.openReferenceModalWithData,
    userInfo: state.userInfo,
  }));

  const { checkCreateElementEnable } = useCheckEnableCreateElement({
    onSuccess: () => {
      const tagId = tagSelectors?.filter((it) => it !== allTag.id) || [];
      openReferenceModalWithData?.({
        referenceModalOpen: true,
        element: {
          modality: 'image',
          labels: tagId.length > 1 ? [] : tags?.filter((it) => it.id === tagId[0]),
        },
        referenceModalMode: 'create',
        referenceModalType: 'reference',
        referenceModalFrom: 'select',
      });
      onCreateOpen?.();
      reportEvent('create_element_page_view', {
        refer_element: 'element_selector',
      });
    },
  });

  const loadMore = useDebounceFn(
    () => {
      onLoadMore?.();
    },
    { wait: 200 },
  );

  const exposureRefs = useRef<HTMLDivElement[]>([]);

  const { initObservedIndices } = useExpose(
    (idx) => {
      onExpose?.(idx);
    },
    exposureRefs.current,
    elements,
    [0.1, 1],
  );

  useEffect(() => {
    initObservedIndices();
  }, [deeps]);

  return (
    <div className={cn(className)}>
      {elements.length === 0 && isLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <Loading className="mx-auto mt-4 animate-spin" />
        </div>
      ) : (
        <AutoSizer>
          {({ width, height }) => {
            const { columnCount, rowHeight } = GRID_PARAMS;
            const columnWidth = Math.floor(width / columnCount);
            const rowCount = Math.ceil(((elements?.length ?? 0) + (enableCreate ? 1 : 0)) / columnCount);
            const gridWidth = width;
            const gridHeight = height;

            return (
              <Grid
                className="hide-scrollbar overflow-x-hidden"
                width={gridWidth}
                height={gridHeight}
                rowCount={rowCount}
                columnCount={columnCount}
                rowHeight={rowHeight} // 行高增加间距
                columnWidth={columnWidth} // 列宽增加间距
                noContentRenderer={() => (
                  <SearchEmpty
                    emptySrc={SEARCH_EMPTY_ICON.selector}
                    textClassName="text-xs"
                    actionText={!userInfo ? t('header.navLogin') : undefined}
                    onAction={
                      !userInfo
                        ? () => {
                            openLoginDialog();
                            reportEvent('login_pop_exposure', {
                              refer_element: 'elements-selector-empty',
                              refer_page: pathname,
                            });
                          }
                        : undefined
                    }
                  />
                )}
                cellRenderer={({ columnIndex, rowIndex, style }) => {
                  const index = rowIndex * columnCount + columnIndex;
                  // 调整style以添加间距
                  const adjustedStyle = {
                    ...style,
                    left: Number(style.left),
                    top: Number(style.top),
                    width: columnWidth,
                    height: rowHeight,
                  };

                  if (index === 0 && enableCreate) {
                    return (
                      <div
                        style={adjustedStyle}
                        key="create-new"
                        onClick={() => {
                          checkCreateElementEnable({
                            query: {},
                          });
                        }}
                      >
                        <div className="flex h-full items-center pl-11">
                          <div className="flex aspect-square w-12 items-center justify-center rounded-10 bg-system-black36">
                            <BaseLinePlus className="text-2xl" />
                          </div>
                          <div className="ml-3 text-lg">{t('elements.elementDialog.title.create')}</div>
                        </div>
                      </div>
                    );
                  }
                  const element = elements[index - (enableCreate ? 1 : 0)];
                  if (!element) return null;
                  const disableSelect = disabledIds?.includes(element.id || '');
                  const checked = selectedElements.some((item) => item.id === element.id) || disableSelect;
                  const isLast = index === elements.length - (enableCreate ? 0 : 1);

                  return (
                    <Fragment key={element.id}>
                      <div
                        style={adjustedStyle}
                        ref={(el: HTMLDivElement) => {
                          exposureRefs.current[index] = el;
                        }}
                        data-index={index}
                      >
                        <div className="flex items-center gap-3 px-2 py-3">
                          <CheckboxFill
                            disabled={disableSelect}
                            checked={checked}
                            customIcon={disableSelect && checked ? <BaseFillCheckPick3 /> : undefined}
                            onCheckedChange={() => {
                              toggleElementSelection?.(element);
                            }}
                            className="flex-shrink-0 text-2xl"
                          />
                          <div
                            className="flex h-12 flex-1 items-center gap-3"
                            onClick={() => {
                              openReferenceModalWithData?.({
                                referenceModalOpen: true,
                                element,
                                referenceModalMode: 'view',
                                referenceModalFrom: 'select',
                                referenceModalFromPage: 'reference_tool',
                              });
                              reportEvent('element_operation', {
                                operation_type: 'view_detail',
                              });
                            }}
                          >
                            <div className="flex flex-1 items-center gap-3">
                              <div className="aspect-square w-12 flex-shrink-0 overflow-hidden rounded-10">
                                {element.modality === 'text' ? (
                                  <TextElementCover name={element.name} className="p-1 text-xs" />
                                ) : (
                                  <>
                                    <MediaPromptPreview
                                      prompt={convertComponents2Assets(element.components)?.[0]}
                                      className="rounded-8"
                                    />
                                    {SITE === 'china' && element.source === 'element_task' && (
                                      <MediaWithWatermark overlay width={1} height={1} showWatermark />
                                    )}
                                  </>
                                )}
                              </div>

                              <div className="line-clamp-1 flex-1 text-lg">{element.name}</div>
                            </div>
                            {element.type === 'preset' && (
                              <div className="flex-shink-0 rounded-full bg-[rgba(0,134,255,0.4)] px-2 py-0.5 text-sm text-white">
                                {t('elements.demo')}
                              </div>
                            )}
                            {element?.type === 'user' && element.tag && (
                              <div className="flex-shink-0 rounded-full bg-[rgba(0,134,255,0.4)] px-2 py-0.5 text-sm text-white">
                                <CustomTooltip showOnlyOnOverflow={true} maxLines={1} content={element.tag}>
                                  {element.tag}
                                </CustomTooltip>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {isLast && hasNextPage && isLoading && <Loading className="mx-auto mt-4 animate-spin" />}
                    </Fragment>
                  );
                }}
                onScroll={({ scrollTop, scrollHeight, clientHeight }) => {
                  if (scrollHeight - scrollTop - clientHeight < 100 && !isLoading && hasNextPage) {
                    loadMore.run();
                  }
                }}
              />
            );
          }}
        </AutoSizer>
      )}
    </div>
  );
};
